import {
  SingleNamedObject,
  NamedObjectContainer,
  getItemByField,
  FixedDimension,
  PercentDimension,
  HtmlGraphicObject,
  SvgRectangleArea
} from "../../web-graphics";
import { IdGenerator } from "../core/id-generator";
import { PlotVerticalLine } from "../core/objects/webgl/plot-vertical-line";
import { Plot } from "../core/objects/svg/plot";

export class OnlineDataPlotObjectGroup {
  constructor(plotObject) {
    this.plotObject = plotObject;

    this.plotObject.itemsSettings.leadRay = Plot.findContentSettings(this.plotObject.settings, "leadRay", "aboveCentral");
    this.plotObject.itemsSettings.marks = Plot.findContentSettings(this.plotObject.settings, "marks", "aboveCentral");
    this.plotObject.itemsSettings.frameCounterErrors = Plot.findContentSettings(
      this.plotObject.settings,
      "frameCounterErrors",
      "aboveCentral"
    );

    this.possibleMaxFramePointCounter = 65536;
    this.marksIdGenerator = new IdGenerator();
    this.counterErrors = [];
    this.counterErrorsContainer = new NamedObjectContainer("CounterErrors");
    this.plotObject.childItemsContainer.add(this.counterErrorsContainer);
    this.markObjects = [];
    this.markObjectsContainer = new NamedObjectContainer("Marks");
    this.plotObject.childItemsContainer.add(this.markObjectsContainer);

    if (!this.plotObject.settings._isNeedChannelLeadRay) {
      this.leadLine = new PlotVerticalLine(
        {
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(0)
          },
          size: {
            width: new FixedDimension(1),
            height: new PercentDimension(1)
          },
          parent: {
            ref: this.plotObject.signalsBox.content.aboveCentral
          },
          color: this.plotObject.itemsSettings.leadRay.color
        }
      );
      this.plotObject.signalsBox.content.aboveCentral.addItem(new SingleNamedObject("LeadLine", this.leadLine));
    }
  }

  startUpdateMeanValues() {
    setInterval(this.plotObject.tryUpdateMeanSignalsValue.bind(this.plotObject), 2000);
  }

  initUpdateMeanValueFunctions(signalObject, dataSourceId, channelId, signalId) {
    if (signalObject.setIsolineChangeAction !== undefined) {
      signalObject.setIsolineChangeAction(
        function (v) {
          this.updateMeanValueText(dataSourceId, channelId, signalId, v);
        }.bind(this.plotObject)
      );
    }
  }

  clearEventObjects() {
    this.counterErrors = [];
    this.counterErrorsContainer.clear();

    this.markObjects = [];
    this.markObjectsContainer.clear();
  }

  setLeadRaysToZero() {
    if (this.plotObject.settings._isNeedChannelLeadRay) {
      this.plotObject.settings.dataSources.forEach(dataSourceSettings => {
        dataSourceSettings.channels.forEach(channelSettings => {
          const leadRay = this.getChannelLead(dataSourceSettings.id, channelSettings.id);
          leadRay.setPosX(0);
        });
      });
    } else {
      this.leadLine.setPosX(0);
    }
  }

  showLeadRays() {
    if (this.plotObject.settings._isNeedChannelLeadRay) {
      this.plotObject.settings.dataSources.forEach(dataSourceSettings => {
        dataSourceSettings.channels.forEach(channelSettings => {
          const leadRay = this.getChannelLead(dataSourceSettings.id, channelSettings.id);
          leadRay.setPosX(0);
          leadRay.setVisibility(true);
        });
      });
    } else {
      this.leadLine.setPosX(0);
      this.leadLine.setVisibility(true);
    }
  }

  hideLeadRays() {
    if (this.plotObject.settings._isNeedChannelLeadRay) {
      this.plotObject.settings.dataSources.forEach(dataSourceSettings => {
        dataSourceSettings.channels.forEach(channelSettings => {
          const leadRay = this.getChannelLead(dataSourceSettings.id, channelSettings.id);
          leadRay.setPosX(0);
          leadRay.setVisibility(false);
        });
      });
    } else {
      this.leadLine.setPosX(0);
      this.leadLine.setVisibility(false);
    }
  }

  createSignalsClickableAreas(channelSettings, channelContainer) {
    if (!channelSettings.showIsolines) {
      return;
    }
    let clickableAreaContainer = new NamedObjectContainer("clickable-areas");
    channelContainer.add(clickableAreaContainer);
    const signalsAreaSize = this.plotObject.getSignalsAreaSize();
    let channelY = channelSettings.y.calcValue(signalsAreaSize.height);
    let channelHeight = channelSettings.height.calcValue(signalsAreaSize.height);
    let currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
    const htmlGraphicObject = HtmlGraphicObject.findHtmlGraphicObject(this.plotObject);

    const signalClickableAreaHeight = 10;
    for (let i = 0; i < currConfiguration.isolines.length; i++) {
      const isolineSettings = currConfiguration.isolines[i];
      const isolineY = channelY + isolineSettings.y.calcValue(channelHeight);

      const signalClickableArea = new SvgRectangleArea(
        {
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(isolineY - signalClickableAreaHeight / 2)
          },
          size: {
            width: new PercentDimension(1),
            height: new FixedDimension(signalClickableAreaHeight)
          },
          parent: {
            ref: this.plotObject.signalsBox.content.aboveCentral
          },
          htmlObject: {
            parent: {
              ref: htmlGraphicObject.settings.htmlObject.ref
            },
            graphicParent: htmlGraphicObject
          }
        }
      );
      signalClickableArea.setBackgroundFillColor("#0000FF11");
      signalClickableArea.addOnClickedFunction(() => {
        this.plotObject.onSignalLabelClicked(channelSettings.id_dataSource, channelSettings.id);
      });
      clickableAreaContainer.add(new SingleNamedObject(isolineSettings.id.toString(), signalClickableArea));
    }
  }

  createSignalsClickableAreaBorders() {
    this.plotObject.dataSourcesContainer.forEachChild(dataSourceContainer => {
      let channelsContainer = dataSourceContainer.findItem("channels");
      if (channelsContainer !== undefined) {
        channelsContainer.forEachChild(channelContainer => {
          let clickableAreaContainer = channelContainer.findItem("clickable-areas");
          if (clickableAreaContainer !== undefined) {
            clickableAreaContainer.forEach(item => item.createBorders());
          }
        });
      }
    });
  }

  createChannelLeadRay(dataSourceId, channelId) {
    if (this.plotObject.settings._isNeedChannelLeadRay) {
      const dataSourcesContainer = this.plotObject._getCreatedChildContainer(
        "data-sources",
        this.plotObject.signalsBox.content.aboveCentral.items
      );
      const dataSourceContainer = this.plotObject._getCreatedChildContainer(dataSourceId.toString(), dataSourcesContainer);
      const channelContainer = this.plotObject._getCreatedChildContainer(channelId.toString(), dataSourceContainer);
      let leadRayItem = channelContainer.findItem("lead-ray");
      if (leadRayItem === undefined) {
        const channelSettings = this.plotObject.getChannelSettings(dataSourceId, channelId);
        const plotCentralHeight = this.plotObject.signalsBox.content.central.settings.sizeInPixels.height;
        const channelY = channelSettings.y.calcValue(plotCentralHeight);
        const channelHeight = channelSettings.height.calcValue(plotCentralHeight);

        const newLeadRay = new PlotVerticalLine(
          {
            location: {
              x: new FixedDimension(0),
              y: new FixedDimension(channelY)
            },
            size: {
              width: new FixedDimension(1),
              height: new FixedDimension(channelHeight)
            },
            parent: {
              ref: this.plotObject.signalsBox.content.aboveCentral
            },
            color: this.plotObject.itemsSettings.leadRay.color
          }
        );
        leadRayItem = new SingleNamedObject("lead-ray", newLeadRay);
        channelContainer.add(leadRayItem);
      }
    }
  }

  updateEventObjects(sourceId, channelId, blockSize, frameCounter) {
    if (this.plotObject.settings._linkedChannel.id_dataSource != sourceId || this.plotObject.settings._linkedChannel.id != channelId) {
      return;
    }
    const signalObject = this.plotObject.getFirstChannelSignalObject(sourceId, channelId);
    if (signalObject === undefined) {
      return;
    }
    const currPointCounter = signalObject.getCurrPointIndex();
    const signalBufferLength = signalObject.getPointsCount();
    this.plotObject.tryRemoveMarks(currPointCounter, blockSize, signalBufferLength);
    this.tryRemoveEventVerticalLines(signalObject, currPointCounter, blockSize);
    this.checkCounterError(sourceId, channelId, signalObject, currPointCounter, blockSize, frameCounter);
  }

  checkCounterError(sourceId, channelId, signalObject, currPointCounter, blockSize, frameCounter) {
    const channelSettings = this.plotObject.getChannelSettings(sourceId, channelId);
    if (channelSettings._expectedPointCounter === undefined) {
      channelSettings._canCheckPossibleMax = true;
      channelSettings._expectedPointCounter = frameCounter;
    }
    let isError = false;
    if (channelSettings._canCheckPossibleMax && channelSettings._expectedPointCounter >= this.possibleMaxFramePointCounter) {
      // ignore counter error
      channelSettings._canCheckPossibleMax = frameCounter < this.possibleMaxFramePointCounter;
    } else if (channelSettings._expectedPointCounter != frameCounter) {
      isError = true;
      // add error mark to plot
      let counterErrorInfo = {
        frameCounter: frameCounter,
        visPointCounter: currPointCounter,
        object: new PlotVerticalLine(
          {
            location: {
              x: new FixedDimension(0),
              y: new FixedDimension(0)
            },
            size: {
              width: new FixedDimension(1),
              height: new PercentDimension(1)
            },
            parent: {
              ref: this.plotObject.signalsBox.content.aboveCentral
            },
            color: this.plotObject.itemsSettings.frameCounterErrors.color
          }
        )
      };
      counterErrorInfo.object.setPosX(signalObject.getCurrPointX());
      this.counterErrors.push(counterErrorInfo);
      this.counterErrorsContainer.add(new SingleNamedObject(`${counterErrorInfo.frameCounter}`, counterErrorInfo.object));
    }
    if (isError) {
      channelSettings._expectedPointCounter = undefined;
    } else {
      channelSettings._expectedPointCounter = frameCounter + blockSize;
    }
  }

  tryRemoveEventVerticalLines(signalObject, currPointCounter, blockSize) {
    const signalBufferLength = signalObject.getPointsCount();
    // remove old marks
    while (
      this.markObjects.length > 0 &&
      this.markObjects[0] !== undefined &&
      this.isNeedRemoveVerticalLine(this.markObjects[0].visPointCounter, currPointCounter, blockSize, signalBufferLength)
    ) {
      this.markObjectsContainer.removeByName(this.markObjects[0].id.toString());
      this.markObjects.shift(); // remove first element
    }

    // remove old counter errors
    while (
      this.counterErrors.length > 0 &&
      this.counterErrors[0] !== undefined &&
      this.isNeedRemoveVerticalLine(this.counterErrors[0].visPointCounter, currPointCounter, blockSize, signalBufferLength)
    ) {
      this.counterErrorsContainer.removeByName(this.counterErrors[0].toString());
      this.counterErrors.shift(); // remove first element
    }
  }

  isNeedRemoveVerticalLine(visCounter, currPointCounter, pointBlockSize, signalBufferLength) {
    if (currPointCounter - pointBlockSize < 0) {
      if (visCounter >= signalBufferLength - pointBlockSize + currPointCounter || visCounter <= currPointCounter) {
        return true;
      }
    } else {
      if (visCounter <= currPointCounter && visCounter > currPointCounter - pointBlockSize) {
        return true;
      }
    }
    return false;
  }

  addMark(data) {
    if (this.plotObject.settings._linkedChannel.id_dataSource === undefined || this.plotObject.settings._linkedChannel.id === undefined) {
      return;
    }

    const signalObject = this.plotObject.getFirstChannelSignalObject(
      this.plotObject.settings._linkedChannel.id_dataSource,
      this.plotObject.settings._linkedChannel.id
    );
    if (signalObject === undefined) {
      return;
    }
    const currPointCounter = signalObject.getCurrPointIndex();
    const markId = this.marksIdGenerator.generateNextId();
    const markInfo = {
      id: markId,
      data: data,
      visPointCounter: currPointCounter,
      object: new PlotVerticalLine(
        {
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(0)
          },
          size: {
            width: new FixedDimension(1),
            height: new PercentDimension(1)
          },
          parent: {
            ref: this.plotObject.signalsBox.content.aboveCentral
          },
          color: this.plotObject.itemsSettings.marks.color
        }
      )
    };
    markInfo.object.setPosX(signalObject.getCurrPointX());
    this.markObjects.push(markInfo);
    this.markObjectsContainer.add(new SingleNamedObject(`${markInfo.id}`, markInfo.object));
  }

  updateLeadRaysPosition(dataSourceId, channelId) {
    if (this.plotObject.settings._isNeedChannelLeadRay) {
      const dataSourceSettings = getItemByField("id", dataSourceId, this.plotObject.settings.dataSources);
      const channelSettings = getItemByField("id", channelId, dataSourceSettings.channels);
      const leadRay = this.plotObject.getChannelLead(dataSourceSettings.id, channelSettings.id);
      let newLeadLinePos = this.plotObject.getSignalCurrPointX(dataSourceSettings.id, channelSettings.id, 0) ?? 0;
      leadRay.setPosX(newLeadLinePos);
    } else {
      if (
        this.plotObject.settings._linkedChannel.id_dataSource != dataSourceId ||
        this.plotObject.settings._linkedChannel.id != channelId
      ) {
        return;
      }
      const signalObject = this.plotObject.getFirstChannelSignalObject(dataSourceId, channelId);
      let newLeadLinePos = signalObject?.getCurrPointX() ?? 0;
      this.leadLine.setPosX(newLeadLinePos);
    }
  }

  getChannelLead(dataSourceId, channelId) {
    const dataSourcesContainer = this.plotObject.signalsBox.content.aboveCentral.items.findItem("data-sources");
    const dataSourceContainer = dataSourcesContainer.findItem(dataSourceId.toString());
    const channelContainer = dataSourceContainer.findItem(channelId.toString());
    const leadRayItem = channelContainer.findItem("lead-ray");
    return leadRayItem?.item;
  }
}
